import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { currencyMaskBRL } from "helpers/utils";
import type { ContaFinanceiraFormPayload } from "../types/contas-financeiras.types";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
  switchLabelSx,
} from "../../components/contas-financeiras.theme";

const TIPOS = [
  { value: "BANCO", label: "Banco" },
  { value: "CAIXA", label: "Caixa" },
  { value: "GATEWAY", label: "Gateway" },
  { value: "OPERACIONAL", label: "Operacional" },
] as const;

type Props = {
  open: boolean;
  editMode: boolean;
  form: ContaFinanceiraFormPayload;
  setForm: Dispatch<SetStateAction<ContaFinanceiraFormPayload>>;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
};

export default function ContaFinanceiraFormDialog({
  open,
  editMode,
  form,
  setForm,
  loading,
  error,
  onClose,
  onSave,
}: Props) {
  const patch =
    (field: keyof ContaFinanceiraFormPayload) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                {editMode ? "Editar conta" : "Nova conta"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {editMode ? form.nome || "Conta financeira" : "Conta financeira"}
              </Typography>
              <Box mt={0.75}>
                <Chip
                  size="small"
                  label={TIPOS.find((t) => t.value === form.tipo)?.label ?? form.tipo}
                  sx={{
                    bgcolor: "rgba(249, 96, 48, 0.85)",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountBalanceWalletOutlinedIcon sx={{ color: CONTAS_FIN_ACCENT }} />
            <MDTypography variant="button" fontWeight="bold">
              Dados da conta
            </MDTypography>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Nome da conta"
            value={form.nome}
            onChange={patch("nome")}
            fullWidth
            required
            size="small"
            placeholder="Ex.: Nubank, Caixa loja, Efí"
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="stretch">
            <FormControl
              variant="outlined"
              size="small"
              sx={{ flex: 1, minWidth: 0, ...fieldSx }}
            >
              <InputLabel shrink htmlFor="conta-fin-tipo">
                Tipo
              </InputLabel>
              <Select
                id="conta-fin-tipo"
                value={form.tipo}
                label="Tipo"
                notched
                input={<OutlinedInput notched label="Tipo" />}
                onChange={(e) => {
                  const tipo = e.target.value as ContaFinanceiraFormPayload["tipo"];
                  setForm((prev) => ({
                    ...prev,
                    tipo,
                    ...(tipo === "GATEWAY" ? { ehContaRepasse: false } : {}),
                  }));
                }}
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Saldo inicial (R$)"
              value={currencyMaskBRL(form.saldoInicial ?? "0")}
              onChange={patch("saldoInicial")}
              fullWidth
              size="small"
              disabled={editMode}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 0, ...fieldSx }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block">
            {editMode
              ? "Saldo inicial não altera após movimentações."
              : "Referência na adoção do extrato."}
          </Typography>

          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                checked={form.ehPadrao}
                onChange={(_, v) => setForm((prev) => ({ ...prev, ehPadrao: v }))}
              />
            }
            label="Definir como conta padrão (sugerida na baixa manual)"
          />

          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                checked={form.ehContaRepasse}
                disabled={form.tipo === "GATEWAY"}
                onChange={(_, v) =>
                  setForm((prev) => ({ ...prev, ehContaRepasse: v }))
                }
              />
            }
            label="Conta de repasse (destino após Efí — exibida ao lado da Efí)"
          />

          {form.tipo === "GATEWAY" ? (
            <Typography variant="caption" color="text.secondary" display="block">
              A conta gateway Efí é origem dos recebimentos, não destino de repasse.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={loading}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => void onSave()}
          disabled={loading || !form.nome.trim()}
          sx={saveButtonSx}
        >
          {loading ? "Salvando…" : editMode ? "Salvar alterações" : "Criar conta"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
